import { FindSelect } from "@/components/input/FindSelect";
import { Button } from "@/components/ui/button";
import { useRoomContext } from "@/context/RoomContext";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import type { LineItemElement } from "@/interfaces/line-item-elements-response";
import type { IXactimateUserRowPayload, LineItemXactimateMapping } from "@/interfaces/payloads/xactimate";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { dispatch } from "@/redux/store";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { LineItemElementsGrid } from "@/components/LineItemElementsGrid";
import { cascadeSelectionUpdate } from "@/components/LineItemElementsGrid.utils";
import CreateLineItemForm from "./CreateLineItemForm";
import { useAddLineItem } from "./hooks/useAddLineItem";
import { useLineItemOptions } from "./hooks/useLineItemOptions";
import { selectionsFromSearchResultLabel } from "@/components/LineItemSearchUtils";
import type { FormulaUnit } from "@rebuild/shared";
import EngineSharedUtils from "./Lines/util/engine-shared-utils";
import { QuantityInput } from "./QuantityInput";

interface AddLineItemProps {
  pageId: string;
  estimateId?: string;
}

const AddLineItem = ({ pageId, estimateId }: AddLineItemProps) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const allowInvalidFormulas = isFeatureEnabled("ALLOW_USER_TO_SAVE_INVALID_FORMULAS");
  const {
    onSearchLineItem,
    createLineItem,
    lineItemXactimateMapping,
    addUserXactimateMapping,
    deleteThenCreateLineItem,
    rateCardUnitByTemplateId,
  } = useEstimateDetail(estimateId!);

  const { duplicateResult, handleDuplicateCheck, clearError, duplicateError } = useRoomContext();

  const {
    onSaveLineItem,
    onChangeQuantity,
    onChangeLineItem,
    reset: resetAddLineItem,
    setOrderIndex,
  } = useAddLineItem(pageId, deleteThenCreateLineItem, createLineItem);

  const {
    onFindOptions,
    options,
    itemSearchSelected,
    setItemSearchSelected,
    onSelectItem,
    matchedPermutation,
    reset,
    showCreateItem,
    setShowCreateItem,
    lineItemTextToCreate,
  } = useLineItemOptions(onSearchLineItem);

  const findSelectRef = useRef<HTMLDivElement>(null);
  const quantityRef = useRef<HTMLDivElement>(null);
  // Permutations we've already requested a mapping for, so we don't refetch on every render.
  const fetchedMappingKeysRef = useRef<Set<string>>(new Set());

  const [elements, setElements] = useState<LineItemElement[]>([]);
  const [selections, setSelections] = useState<string[]>([]);
  const [isQuantityValid, setIsQuantityValid] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);

  const isExpanded = !!itemSearchSelected || searchTerm.length > 0;

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const input = findSelectRef.current?.querySelector("input");
      input?.focus();
    });
  }, []);

  useEffect(() => {
    if (itemSearchSelected) {
      requestAnimationFrame(() => {
        quantityRef.current?.querySelector("input")?.focus();
      });
    }
  }, [itemSearchSelected]);

  const onSaveLineItemHandler = useCallback(async () => {
    if (!isQuantityValid) return;

    // Clear before await so stale selections don't re-trigger the duplicate check
    // when room data refetches after save
    setSelections([]);
    setSearchTerm("");
    clearError?.();
    reset();

    if (duplicateResult.location === "recommended" && duplicateResult.duplicateItem) {
      await onSaveLineItem({
        tupleId: duplicateResult.duplicateItem.tuple.tupleId,
        pageId,
        uniqueId: duplicateResult.duplicateItem.currentItem.uniqueId,
        category: "recommended",
      });
    } else {
      await onSaveLineItem();
    }
    setIsQuantityValid(true);
    focusInput();
  }, [isQuantityValid, duplicateResult.location, duplicateResult.duplicateItem, clearError, reset, onSaveLineItem, pageId, focusInput]);

  useEffect(() => {
    if (itemSearchSelected) {
      setElements(itemSearchSelected.elements);
      setOrderIndex(itemSearchSelected.orderIndex);
      const presetLabel = matchedPermutation();
      const nextSelections = selectionsFromSearchResultLabel(itemSearchSelected.elements, presetLabel);
      onChangeLineItem(nextSelections.join(" ").trim(), itemSearchSelected.id);
      setSelections(nextSelections);
      setSearchTerm(nextSelections.join(" ").trim());
    } else {
      setElements([]);
    }
  }, [itemSearchSelected, estimateId, onChangeLineItem, setOrderIndex, matchedPermutation]);

  useEffect(() => {
    if (selections.length > 0) {
      handleDuplicateCheck?.(selections.join(" ").trim());
    }
  }, [selections, handleDuplicateCheck]);

  const updateSelection = (index: number, newValue: string) => {
    setSelections((prev) => {
      const updated = [...prev];
      updated[index] = newValue?.trim();
      const cascaded = cascadeSelectionUpdate(elements, updated);
      const updatedLineItem = cascaded.join(" ").trim();
      onChangeLineItem?.(updatedLineItem);
      setSearchTerm(updatedLineItem);
      return cascaded;
    });
  };

  const onCreateItemClick = useCallback(() => {
    setShowCreateItem(true);
  }, [setShowCreateItem]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && itemSearchSelected) {
      onSaveLineItemHandler();
    }
    if (e.key === "Escape" && isExpanded) {
      setSearchTerm("");
      clearError?.();
      reset();
      resetAddLineItem();
    }
  };

  const onCreateLineItem = useCallback(
    async (mapping: IXactimateUserRowPayload, quantityString?: string): Promise<boolean> => {
      const response = await addUserXactimateMapping(mapping);

      if (!response.success || !response.lineItem) {
        return false;
      }

      if (quantityString) {
        const formula = EngineSharedUtils.extractFormulaFromString(quantityString);
        await createLineItem(
          pageId,
          response.lineItem.originalLineItem,
          response.lineItem.id,
          formula?.initialValue ?? 0,
          formula?.base,
          formula,
          response.lineItem.orderIndex,
        );

        if (response.mapping) {
          dispatch(
            estimationActions.setEstimationState({
              lineItemXactimateMapping: [
                ...lineItemXactimateMapping,
                {
                  lineItemId: response.lineItem.id,
                  lineItem: response.lineItem.originalLineItem,
                  cat: response.mapping.cat,
                  sel: response.mapping.variable,
                  activity: response.mapping.activity,
                  quantity: response.mapping.quantity,
                } as LineItemXactimateMapping,
              ],
            }),
          );
        }

        reset();
        setSearchTerm("");
        focusInput();
      } else {
        setItemSearchSelected(response.lineItem);
      }

      return true;
    },
    [addUserXactimateMapping, setItemSearchSelected, lineItemXactimateMapping, createLineItem, pageId, reset, focusInput],
  );

  const correctUnit = useMemo(() => {
    if (!itemSearchSelected) return undefined;
    const allowedUnits = EngineSharedUtils.getAllowedUnitsFromXactimateMapping(
      { lineItem: searchTerm, lineItemId: itemSearchSelected.id } as any,
      lineItemXactimateMapping ?? [],
    );
    // T&M line items have no xactimate mapping; their unit lives on the rate item
    // and is keyed by lineItemTemplate.id (which is itemSearchSelected.id here).
    const rateCardUnit = rateCardUnitByTemplateId?.[itemSearchSelected.id]?.toUpperCase();
    return (allowedUnits[0] ?? rateCardUnit)?.toUpperCase() as FormulaUnit | undefined;
  }, [itemSearchSelected, searchTerm, lineItemXactimateMapping, rateCardUnitByTemplateId]);

  // Fetch the mapping when it isn't in the loaded subset, so the unit shows in the QTY input.
  useEffect(() => {
    if (!itemSearchSelected || correctUnit) return;
    const name = searchTerm.trim();
    if (!name) return;
    const key = `${itemSearchSelected.id}:${name.replace(/\s/g, "").toLowerCase()}`;
    if (fetchedMappingKeysRef.current.has(key)) return;
    fetchedMappingKeysRef.current.add(key);
    dispatch(estimationActions.updateXactimateMappingForLineItem(name, itemSearchSelected.id));
  }, [itemSearchSelected, searchTerm, correctUnit]);

  const handleCancel = useCallback(() => {
    setSearchTerm("");
    clearError?.();
    reset();
    resetAddLineItem();
  }, [clearError, reset, resetAddLineItem]);

  return (
    <div>
      {showCreateItem ? (
        <CreateLineItemForm lineItemText={lineItemTextToCreate} onCreateLineItem={onCreateLineItem} setShowCreateItem={setShowCreateItem} />
      ) : (
        <div ref={containerRef} tabIndex={-1} onKeyDown={onKeyDown} className="border-b border-[#EEEFF1]">
          <div className="grid h-[52px] grid-cols-9 items-center px-4 py-2">
            <div className="col-span-4 flex items-center pl-5">
              <Plus className="mr-2 h-4 w-4 shrink-0 text-[#A1A1AA]" />
              <div className="flex-1" ref={findSelectRef}>
                <FindSelect
                  showOptions="bottom"
                  options={options}
                  onChange={onSelectItem}
                  onFindOptions={onFindOptions}
                  placeholder="Add line item..."
                  onCreateItem={onCreateItemClick}
                  error={duplicateError}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  inputClassName="h-7 w-full border-0 bg-transparent px-2 py-1 text-sm font-medium shadow-none ring-0 placeholder:text-[#A1A1AA] focus:outline-none focus-visible:ring-0"
                  onClear={() => {
                    clearError?.();
                  }}
                />
              </div>
            </div>

            {isExpanded && (
              <>
                <div className="col-span-3 flex flex-row justify-end px-8">
                  <QuantityInput
                    ref={quantityRef}
                    onChange={onChangeQuantity}
                    onValidityChange={setIsQuantityValid}
                    correctUnit={correctUnit}
                    disabled={!itemSearchSelected}
                    allowInvalidFormulas={allowInvalidFormulas}
                  />
                </div>
                <div className="col-span-2 flex flex-row items-center justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={onSaveLineItemHandler}>
                    Confirm
                  </Button>
                  <Trash2 className="h-4 w-4 cursor-pointer" onClick={handleCancel} color="#E45F58" />
                </div>
              </>
            )}
          </div>

          {isExpanded && elements.length > 0 && (
            <div className="pt-0 pr-4 pb-3 pl-11">
              <LineItemElementsGrid elements={elements} selections={selections} updateSelection={updateSelection} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddLineItem;
